import { api } from './api.config';

import { Class, ProgressionRule } from './../types/tournament';
import { ClassTeam } from '../pages/tourDetails/TournamentClasses/components/ClassesTag/ClassesTag';
import { SplitTeamResponseDto } from '../generated/orval/schemas';
import { GroupStandingsTieBreakerEnum } from '../types/enums';

export const fetchClassByIdData = async (id: number): Promise<Class> => {
  const response = await api.get(`tournament/class/${id}`);
  return response.data;
};

export const fetchClassPlayData = async (id: number): Promise<Class> => {
  const response = await api.get(`tournament/class/${id}/play`);
  return response.data;
};

export const fetchClassTeamsData = async (id: number): Promise<Class> => {
  const response = await api.get(`tournament/class/${id}/teams`);
  return response.data;
};

export const fetchClassTournamentGroupsData = async (id: number): Promise<Class> => {
  const response = await api.get(`tournament/class/${id}/tournament-groups`);
  return response.data;
};

export const fetchClassMatchesData = async (id: number): Promise<Class> => {
  const response = await api.get(`tournament/class/${id}/tournament-matches`);
  return response.data;
};

export const createClassData = async (tornamentId: number, data: any): Promise<Class> => {
  const response = await api.post(`tournament/${tornamentId}/class`, data);
  return response.data;
};

export const updateClassData = async (
  tornamentId: number,
  classId: number,
  data: any
): Promise<Class> => {
  // TODO: temporary fields: organization & creator, because out of registration
  const response = await api.patch(`tournament/${tornamentId}/class/${classId}`, data);
  return response.data;
};

export const fetchGroupStandings = async (classId: number, groupId: number, round: number) => {
  const response = await api.get(`tournament/class/${classId}/group/${groupId}/standings/${round}`);
  // response.data is array of team standing objects
  return response.data;
};

export type StandingsExplainQuery = {
  includeGroup?: boolean;
  includeElimination?: boolean;
};

export type StandingsExplainStatsRow = {
  id: number;
  name: string;
  position: number;
  countMatches?: number;
  matchWin?: number;
  matchLoss?: number;
  gameWin?: number;
  pointsDiff?: number;
  pointsWon?: number;
  points?: number;
  teamWin?: number;
  teamDraw?: number;
  teamLoss?: number;
  teamPoints?: number;
  countTeamMeetings?: number;
};

export type StandingsExplainStep = {
  criterion: GroupStandingsTieBreakerEnum;
  groupsBefore: number[][];
  subgroups: Array<{
    teamIds: number[];
    metricsByTeamId: Record<number, number>;
    splitInto: number[][];
    note?: string;
  }>;
  groupsAfter: number[][];
};

export type StandingsExplainGroup = {
  groupId: number;
  groupName: string;
  initialRows: Array<Record<string, unknown>>;
  steps: StandingsExplainStep[];
  finalRanking: Array<
    StandingsExplainStatsRow & {
      wonLottery?: number[];
      lostLottery?: number[];
    }
  >;
};

export type StandingsExplainResponse = {
  groupStage?: {
    supported: boolean;
    groups?: StandingsExplainGroup[];
    reason?: string;
    message?: string;
  };
  elimination?: {
    supported: boolean;
    reason?: string;
    message?: string;
    perGroupSummary?: Array<Record<string, unknown>>;
    mainBracket?: { matchups: unknown[]; rankedList: unknown[]; trace: unknown[] } | null;
    consolationBracket?: { matchups: unknown[]; rankedList: unknown[]; trace: unknown[] } | null;
    seedingType?: number | string;
    eliminationDraw?: number | string;
    consolationDraw?: number | string;
    hasConsolation?: boolean;
  };
  tieBreakers?: {
    effective: GroupStandingsTieBreakerEnum[];
    source: string;
    chain: Record<string, unknown>;
  };
  classType?: number | string | null;
};

export const fetchStandingsExplain = async (
  classId: number,
  query?: StandingsExplainQuery,
): Promise<StandingsExplainResponse> => {
  const params: Record<string, boolean> = {};
  if (query?.includeGroup === false) params.includeGroup = false;
  if (query?.includeElimination === false) params.includeElimination = false;
  const response = await api.get<StandingsExplainResponse>(`tournament/class/${classId}/standings-explain`, {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

export const assignTeamsForElimination = async (classId: number): Promise<Class> => {
  const response = await api.post(`tournament/class/${classId}/assign-teams`);
  return response.data;
};

export const devAutoScore = async (classId: number): Promise<Class> => {
  const response = await api.patch(`tournament/class/${classId}/devAutoScore`);
  return response.data;
};

export const transferTeamToClass = async (classId: number, teamId: number): Promise<Class> => {
  const response = await api.post(`tournament/team/${teamId}/transfer-to-class/${classId}`);
  return response.data;
};

export const getTournamentClassesWithTeams = async (tournamentId: number): Promise<Class[]> => {
  const response = await api.get(`tournament/${tournamentId}/classes-with-teams`);
  return response.data;
};

export const combineTeams = async (
  sourceTeamId: number,
  targetTeamId: number
): Promise<ClassTeam> => {
  const response = await api.post(
    `tournament/team/${sourceTeamId}/combine-with-team/${targetTeamId}`
  );
  return response.data;
};

export const splitTeam = async (teamId: number): Promise<SplitTeamResponseDto> => {
  const response = await api.post('admin/ticket/split-team', { teamId });
  return response.data;
};

export const updateProgressionRules = async (classId: number, rules: ProgressionRule[]) => {
  const response = await api.patch(`tournament/class/${classId}/updateProgressionRules`, rules);
  return response.data;
};